'use client'

// React
import { useState, createContext, useContext } from 'react'

// Animation
import { motion, AnimatePresence } from 'framer-motion'

// Docs components
import { DocSidebar } from './doc-sidebar'
import { TableOfContents } from './table-of-contents'

// Lib
import { DocNavItem, SearchIndexItem } from '../../lib/docs-nav'

interface AnimatedDocsLayoutProps {
  navigation: DocNavItem[]
  searchIndex: SearchIndexItem[]
  /** Mobile drawer heading (desktop sidebar has no title). */
  sidebarTitle?: string
  children: React.ReactNode
}

interface SidebarContextType {
  toggleSidebar: () => void
  /** Nav tree for breadcrumbs / other chrome that must not import docs-nav (fs). */
  navigation: DocNavItem[]
}

const SidebarContext = createContext<SidebarContextType | null>(null)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within AnimatedDocsLayout')
  }
  return context
}

export function AnimatedDocsLayout({
  navigation,
  searchIndex,
  sidebarTitle = 'Documentation',
  children,
}: AnimatedDocsLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <SidebarContext.Provider
      value={{
        toggleSidebar: () => setSidebarOpen(!sidebarOpen),
        navigation,
      }}
    >
      {/* `flex-1` makes the docs column fill the parent flex column when used
       *  inside `<DocsShell>` (or any `flex min-h-screen flex-col` shell). The
       *  `min-w-0` is the standard escape hatch so wide MDX content doesn't
       *  blow out the grid. */}
      <div className="relative mx-auto flex w-full max-w-360 flex-1 flex-col px-4 py-8 lg:px-8">
        <div className="mx-auto grid w-full flex-1 grid-cols-1 gap-8 md:grid-cols-[256px_auto] lg:grid-cols-[256px_auto_256px] xl:grid-cols-[256px_660px_256px]">
          {/* Sidebar - Desktop */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden w-64 md:block"
          >
            <DocSidebar navigation={navigation} searchIndex={searchIndex} />
          </motion.aside>

          {/* Sidebar - Mobile overlay */}
          <AnimatePresence>
            {sidebarOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 z-40 cursor-pointer bg-black/20 md:hidden dark:bg-black/40"
                  onClick={() => setSidebarOpen(false)}
                />
                <motion.aside
                  initial={{ opacity: 0, x: -280 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -280 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="fixed top-0 bottom-0 left-0 z-50 w-64 overflow-y-auto border-r border-slate-200 bg-slate-100 p-6 shadow-xl md:hidden dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {sidebarTitle}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="cursor-pointer rounded-md p-1 hover:bg-slate-200 dark:hover:bg-slate-800"
                      aria-label="Close sidebar"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <DocSidebar
                    navigation={navigation}
                    searchIndex={searchIndex}
                    onLinkClick={() => setSidebarOpen(false)}
                  />
                </motion.aside>
              </>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            // Floor: ensures short pages (e.g. `/docs/api/`) still fill the
            // viewport when this component is rendered standalone (no
            // `flex-1`-providing parent). `dvh` accounts for mobile chrome,
            // and `7rem` budgets ~Header (4rem) + minimal Footer (~3rem).
            className="min-h-[calc(110vh)] min-w-0"
          >
            {children}
          </motion.main>

          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="hidden lg:block"
          >
            <TableOfContents />
          </motion.div>
        </div>
      </div>
    </SidebarContext.Provider>
  )
}
